
import {of, Subscription} from 'rxjs';

import {delay, map, switchMap } from 'rxjs/operators';
import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";
import {INotice} from "../types";
import {Notice, NoticeDropdowns, NoticeService, ClientService, TaxYearService} from "../../../../swaggerServices";
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {IListOption, DropdownListOption} from "../../../shared/components/search-dropdown/search-dropdown.component";
import moment from "moment";

declare var $: any;


@Component({
    selector: 'app-notice-modals',
    templateUrl: './notice-modals.component.html',
    styleUrls: ['./notice-modals.component.scss'],
    standalone: false
})
export class NoticeModalsComponent implements OnInit, OnDestroy {
  // common loading indicator for continue/reprocess
  processing: boolean = false;
  @Input() taxpayerAlias: string;
  @ViewChild('reprocessModal') reprocessModalEl: ElementRef;
  reprocessNotices?: INotice[];
  @Output() reprocessingSubmitted: EventEmitter<INotice[]> = new EventEmitter<INotice[]>();

  @ViewChild('continueModal') continueModalEl: ElementRef;
  continueNotice?: INotice;
  @Output() continueProcessingSubmitted: EventEmitter<INotice> = new EventEmitter<INotice>();

  // adding/editing a notice
  @ViewChild('noticeFormModal') noticeFormModalEl: ElementRef;
  editingNoticeId?: number;
  noticeFormGroup?: UntypedFormGroup;
  formSubs: Subscription[] = [];
  loadingDropdowns?: boolean;
  noticeDropdowns?: NoticeDropdowns;
  periodListOptions?: IListOption[];
  noticeTypeListOptions?: IListOption[];
  showFullForm = true;
  duplicateNotice = null;
  clientsList;
  @Output() noticeSaved: EventEmitter<void> = new EventEmitter<void>();

  // generic success modal
  @ViewChild('successModal') successModalEl: ElementRef;
  successMessage?: string;

  constructor(private noticeService: NoticeService,
    private clientService: ClientService,
    private taxYearsService: TaxYearService) { }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.formSubs.forEach(s => s.unsubscribe());
  }

  openReprocessModal(notices: INotice[]) {
    this.reprocessNotices = notices;
    $(this.reprocessModalEl.nativeElement).modal('show');
  }

  confirmReprocessing() {
    if (this.reprocessNotices) {
      const ns = this.reprocessNotices;
      this.processing = true;
      const noticeIds: Array<number> = ns.map(n => n.id);
      this.noticeService.reprocessNotices({ noticeIds }).subscribe(() => {
        this.processing = false;
        this.reprocessingSubmitted.emit(ns);
        $(this.reprocessModalEl.nativeElement).modal('hide');
      }, e => {
        this.processing = false;
      });
    }
  }

  openContinueModal(notice: INotice) {
    this.continueNotice = notice;
    $(this.continueModalEl.nativeElement).modal('show');
  }

  confirmContinue() {
    if (this.continueNotice) {
      const n = this.continueNotice;
      const nextStepId = n.step.nextStepId;
      this.noticeService.continueProcessingNotice(n.id, { nextStepId }).subscribe(() => {
        this.processing = false;
        this.continueProcessingSubmitted.emit(n);
        $(this.continueModalEl.nativeElement).modal('hide');
      }, e => {
        this.processing = false;
      });
    }
  }

  openCreateUpdateNoticeModal(existingNotice?: Notice) {
    this.noticeFormGroup = null;
    this.editingNoticeId = existingNotice ? existingNotice.id : null;

    this.showFullForm = !!this.editingNoticeId;
    if (!this.noticeDropdowns) {
      this.loadingDropdowns = true;
      return this.noticeService.getNoticeDropdowns().pipe(map(dropdowns => {
        this.loadingDropdowns = false;
        this.noticeDropdowns = dropdowns;
        this.periodListOptions = this.noticeDropdowns.period.map((p, i) => {
          const dd: IListOption = {
            id: i,
            display: p,
            value: p,
            order: i,
          };
          return dd;
        });
        this.noticeTypeListOptions = this.noticeDropdowns.noticeType.map((nt, i) => {
          const dd: IListOption = {
            id: nt.id,
            display: nt.name,
            value: nt.id.toString(),
            order: i,
          };
          return dd;
        });
        this.initFormGroup(existingNotice);
        $(this.noticeFormModalEl.nativeElement)
          .modal({
            approve  : '.approve',
            deny     : '.cancel'
          }).modal('show');
      }));
    } else {
      // Allow time for the form group to be deleted and re-created
      return of({}).pipe(delay(0),map(() => {
        this.initFormGroup(existingNotice);
        $(this.noticeFormModalEl.nativeElement)
        .modal({
          approve  : '.approve',
          deny     : '.cancel'
        }).modal('show');
      }),);
    }
  }

  checkDocumentId(docId) {

    this.duplicateNotice = null;
    this.noticeService.findByDocumentId(docId).subscribe(result => {
      if (result) {
        this.duplicateNotice = result;
        const noticeType = this.noticeTypeListOptions.find(n => n.id === this.duplicateNotice.noticeTypeId);
        this.duplicateNotice.noticeTypeName = noticeType ? noticeType.display : '';
      } else {
        this.noticeFormGroup.get('documentId').setValue(docId);
        this.showFullForm = true;
      }
    });


  }

  initFormGroup(existingNotice?: Notice) {
    const existingData = existingNotice || {};

    const partyEntityName = new UntypedFormControl(
      existingData.partyEntityName,
      { validators: [Validators.required] },
    );
    const noticeClientId = new UntypedFormControl(
      existingData.clientId,
    );
    const documentId = new UntypedFormControl(
      existingData.documentId,
      { validators: [Validators.required] },
    );
    const noticeType = new UntypedFormControl(
      // existingData.noticeTypeId ? this.noticeTypeListOptions.find(opt => opt.id === existingData.noticeTypeId) : null,
      existingData.noticeTypeId ? existingData.noticeTypeId.toString() : null,
      { validators: [Validators.required] },
    );
    const dueDate = new UntypedFormControl(
      this.parseDueDate(existingData.dueDate),
      { validators: [] },
    );
    const year = new UntypedFormControl(
      existingData.year,
      { validators: [Validators.required] },
    );
    const period = new UntypedFormControl(
      existingData.period || null,
      { validators: [] },
    );
    const tfn = new UntypedFormControl(
      existingData.documentId,
      { validators: [Validators.required] },
    );
    const abn = new UntypedFormControl(
      existingData.documentId,
    );
    const date = new UntypedFormControl(
      this.parseDueDate(existingData.date),
      { validators: [] },
    );
    this.loadClientList().subscribe(clients => {
      this.clientsList = clients;
      this.noticeFormGroup = new UntypedFormGroup({
        "partyEntityName": partyEntityName,
        "noticeClientId": noticeClientId,
        "documentId": documentId,
        "noticeType": noticeType,
        "dueDate": dueDate,
        "year": year,
        "period": period,
        "tfn": tfn,
        "abn": abn,
        "date": date
     });
    });


    // Allow leading 0s, I'm unsure of ABN or TFN can have a leading 0
    // which number fields don't handle. Also, number fields don't
    // look that nice with the up/down picker.
    const ensureNumbersOnly = formCtrl => {
      this.formSubs.push(formCtrl.valueChanges.subscribe(val => {
        const modified = val.replace(/[^0-9]/g, "");
        if (modified !== val) {
          formCtrl.setValue(modified);
        }
      }));
    };

    this.formSubs.forEach(s => s.unsubscribe());
    ensureNumbersOnly(documentId);
    ensureNumbersOnly(year);
    ensureNumbersOnly(tfn);
    ensureNumbersOnly(abn);
  }

  saveNotice() {
    const {
      documentId,
      dueDate,
      noticeType, // {id: 6, display: "Debt - Overdue reminder", value: "6", order: 5}
      partyEntityName,
      noticeClientId,
      period, // {id: 1, display: "Q1", value: "Q1", order: 1}
      year,
      tfn,
      abn,
      date
    } = this.noticeFormGroup.value;

    // noticeType and period are dropdowns
    const periodVal = period ? period.value : null;
    const noticeTypeId = noticeType ? noticeType.id : null;
    const clientId = noticeClientId && noticeClientId.id ? Number(noticeClientId.id) : null;

    const formObj = {
      documentId: Number(documentId),
      dueDate,
      noticeTypeId: Number(noticeTypeId),
      partyEntityName,
      clientId,
      period: periodVal,
      year: Number(year),
      date
    };
    if (this.editingNoticeId) {
      this.noticeService.updateNotice(this.editingNoticeId, formObj).subscribe(() => {
        this.noticeSaved.emit();
        this.successMessage = "Notice successfully updated";
        $(this.successModalEl.nativeElement).modal('show');
      });
    } else {
      this.noticeService.createNotice({ ...formObj, tfn, abn }).subscribe(() => {
        this.noticeSaved.emit();
        this.successMessage = "Notice successfully created";
        $(this.successModalEl.nativeElement).modal('show');
      });
    }
  }

  private loadClientList() {
    return this.taxYearsService.getTaxYears()
    .pipe(
      switchMap((years: any) => {
        const latestYear = years.map(y => y.year).sort().reverse().shift();
        return this.clientService.getClients(latestYear);
      }),
      map(clients => {
        return clients.map((c, i)=>{
          return {
            id: c.id,
            display: c.name,
            value: c.id.toString(),
            order: i,
          };
        })
      })
    );
  }

  private parseDueDate(d) {
    if (!d) {
      return null;
    }
    const m = moment(d);
    if (!m.isValid()) {
      return null;
    }
    return m.format("YYYY-MM-DD");
  };
}
